import { useApolloClient } from '@apollo/client/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { A2uiUserAction } from '~/components/AIInsightsModal/AIInsightsModal.utils';
import A2uiSurface from '~/components/AIInsightsUI/a2ui/A2uiSurface';
import { PeopleCountLine, usePulseSurfacePeopleCount } from '~/components/AIInsightsUI/a2ui/PeopleCount';
import Button from '~/components/Button';
import SecondaryButton from '~/components/Button/SecondaryButton';
import { AgentSecureInputKeyDocument, AgentSecureInputKeyQuery } from '~/types/api.graphql';

import { translateSummary, translateWarning } from './confirmationCopy';
import {
  buildFieldList,
  CardField,
  coerceFieldValue,
  formatDisplayFieldValue,
  isBooleanField,
  isNumericField,
  isNumericValueValid,
  isStructuredField,
} from './confirmationFields';
import {
  buildConfirmationSurface,
  CANCEL_ACTION_NAME,
  localizeSurface,
  surfaceHasCancelAction,
} from './confirmationSurface';
import {
  MutationConfirmation,
  MutationConfirmationAnswer,
  MutationConfirmationResponse,
} from './mutationConfirmation.types';
import {
  CardActions,
  CardContainer,
  CardError,
  CardStatus,
  CardSummary,
  CardTitle,
  CardWarning,
  FieldCheckbox,
  FieldInput,
  FieldJsonValue,
  FieldLabel,
  FieldRow,
  FieldsList,
} from './MutationConfirmationCard.styles';
import { encryptSecureInput } from './secureInput';
import { useCardPulseSelection } from './useCardPulseSelection';

interface Props {
  confirmation: MutationConfirmation;
  conversationId: string | null;
  // Answer already given this session (renders the card as answered/disabled).
  answer?: MutationConfirmationAnswer;
  // Stale cards (earlier turns) and cards during an in-flight stream are inert.
  isActionable: boolean;
  onRespond: (response: MutationConfirmationResponse) => void | Promise<boolean>;
}

const MutationConfirmationCard = ({ confirmation, conversationId, answer, isActionable, onRespond }: Props) => {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();

  const [editedValues, setEditedValues] = useState<Record<string, string | boolean>>({});
  const [secretValues, setSecretValues] = useState<Record<string, string>>({});
  const [isExpired, setIsExpired] = useState(() => hasExpired(confirmation.expiresAt));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fields = useMemo(() => buildFieldList(confirmation, t), [confirmation, t]);
  const sensitiveFields = fields.filter((field) => field.sensitive);

  const surfaceId = confirmation.surfaceId ?? `mutation-confirmation-${confirmation.interruptId}`;
  const confirmLabel = t('aiInsights.mutationConfirmation.confirm');
  const cancelLabel = t('aiInsights.mutationConfirmation.cancel');
  const operations = useMemo(() => {
    const labels = { confirm: confirmLabel, cancel: cancelLabel };
    if (confirmation.a2uiOperations?.length) {
      return localizeSurface({ operations: confirmation.a2uiOperations, labels, fields });
    }
    try {
      return buildConfirmationSurface({ fields, surfaceId, labels });
    } catch {
      return null;
    }
  }, [confirmation.a2uiOperations, fields, surfaceId, confirmLabel, cancelLabel]);

  // An unanswered card past the gate timeout shows as expired instead of staying actionable.
  useEffect(() => {
    if (!confirmation.expiresAt || answer || isExpired) return undefined;
    const remainingMs = confirmation.expiresAt * 1000 - Date.now();
    const timer = setTimeout(() => setIsExpired(true), Math.max(remainingMs, 0));
    return () => clearTimeout(timer);
  }, [confirmation.expiresAt, answer, isExpired]);

  const canAnswer = isActionable && !answer && !isExpired && !isSubmitting;

  // People count of the Pulse selection, live as the user edits the card's topics. Answered and
  // expired cards don't query; they keep a count only if it is already cached.
  const pulseSelection = useCardPulseSelection({ confirmation, fields, operations, active: canAnswer });
  const { peopleCount, onDataModelChange } = usePulseSurfacePeopleCount({ ...pulseSelection, active: canAnswer });

  const hasInvalidField = useMemo(
    () => fields.some((field) => isFieldInvalid(field, editedValues, secretValues)),
    [fields, editedValues, secretValues],
  );

  const submitDenied = useCallback(async (): Promise<boolean> => {
    setError(null);
    const dispatched = await onRespond({ interruptId: confirmation.interruptId, answer: 'deny' });
    if (dispatched === false) {
      setError(t('aiInsights.mutationConfirmation.notDispatched'));
      return false;
    }
    return true;
  }, [confirmation.interruptId, onRespond, t]);

  const handleCancel = useCallback(() => {
    if (!canAnswer) return;
    void submitDenied();
  }, [canAnswer, submitDenied]);

  const submitConfirmed = useCallback(
    async (readValue: (field: CardField) => unknown, readSecret: (field: CardField) => string): Promise<boolean> => {
      setError(null);

      const values: Record<string, unknown> = {};
      for (const field of fields) {
        // Fixed values are never sent back: the gate keeps the model's value for them.
        if (field.sensitive || field.fixed) continue;
        values[field.name] = readValue(field);
      }

      const missingRequired = fields.find(
        (field) => !field.sensitive && !field.fixed && isValueMissing(values[field.name], field),
      );
      if (missingRequired) {
        setError(t('aiInsights.mutationConfirmation.missingRequired', { field: missingRequired.label }));
        return false;
      }

      const missingSecret = sensitiveFields.find((field) => field.required && !readSecret(field).trim());
      if (missingSecret) {
        setError(t('aiInsights.mutationConfirmation.missingSecret', { field: missingSecret.label }));
        return false;
      }

      setIsSubmitting(true);
      try {
        const providedSecrets = sensitiveFields.filter((field) => readSecret(field).trim());
        if (providedSecrets.length > 0) {
          const secureInputKey = await fetchSecureInputKey(apolloClient);
          if (!secureInputKey) {
            setError(t('aiInsights.mutationConfirmation.secureInputUnavailable'));
            return false;
          }
          const bindings = Object.fromEntries(
            Object.entries(values)
              .filter((entry) => entry[1] !== null && entry[1] !== undefined && typeof entry[1] !== 'object')
              .map(([key, val]) => [key, String(val)]),
          );
          for (const field of providedSecrets) {
            values[field.name] = await encryptSecureInput(secureInputKey, readSecret(field), {
              tool: confirmation.tool,
              conversationId: conversationId ?? '',
              bindings,
            });
          }
        }

        const dispatched = await onRespond({ interruptId: confirmation.interruptId, answer: 'confirm', values });
        if (dispatched === false) {
          setError(t('aiInsights.mutationConfirmation.notDispatched'));
          return false;
        }
        return true;
      } catch {
        setError(t('aiInsights.mutationConfirmation.encryptionFailed'));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [fields, sensitiveFields, apolloClient, confirmation, conversationId, onRespond, t],
  );

  const handleSurfaceAction = useCallback(
    (action: A2uiUserAction): Promise<boolean> => {
      if (!canAnswer) return Promise.resolve(false);
      if (action.name === CANCEL_ACTION_NAME) return submitDenied();
      const context = action.context ?? {};
      return submitConfirmed(
        (field) => (field.name in context ? coerceFieldValue(context[field.name], field) : field.value),
        (field) => (typeof context[field.name] === 'string' ? (context[field.name] as string) : ''),
      );
    },
    [canAnswer, submitDenied, submitConfirmed],
  );

  const handleHandRolledConfirm = useCallback(() => {
    if (!canAnswer || hasInvalidField) return;
    void submitConfirmed(
      (field) => (field.name in editedValues ? coerceFieldValue(editedValues[field.name], field) : field.value),
      (field) => secretValues[field.name] ?? '',
    );
  }, [canAnswer, hasInvalidField, submitConfirmed, editedValues, secretValues]);

  const answeredLabel =
    answer &&
    t(answer === 'confirm' ? 'aiInsights.mutationConfirmation.confirmed' : 'aiInsights.mutationConfirmation.cancelled');
  const statusLabel = answeredLabel || (isExpired ? t('aiInsights.mutationConfirmation.expired') : null);
  const showCardActions = !operations || !surfaceHasCancelAction(operations);

  return (
    <CardContainer $inactive={!canAnswer}>
      <CardTitle>{t('aiInsights.mutationConfirmation.title')}</CardTitle>
      {confirmation.summary && (
        <CardSummary>{translateSummary(t, confirmation.tool, confirmation.summary)}</CardSummary>
      )}
      {confirmation.warning && (
        <CardWarning>{translateWarning(t, confirmation.tool, confirmation.warning)}</CardWarning>
      )}

      {operations ? (
        <A2uiSurface
          operations={operations}
          surfaceId={surfaceId}
          onSubmit={handleSurfaceAction}
          disabled={!canAnswer}
          onDataModelChange={onDataModelChange}
        />
      ) : (
        <FieldsList>
          {fields.map((field) => (
            <FieldRow key={field.name}>
              <FieldLabel>
                {field.label}
                {field.required ? ' *' : ''}
              </FieldLabel>
              {renderFieldInput(field, {
                canAnswer,
                editedValues,
                secretValues,
                setEditedValues,
                setSecretValues,
                secretPlaceholder: t('aiInsights.mutationConfirmation.secretPlaceholder'),
              })}
            </FieldRow>
          ))}
        </FieldsList>
      )}

      {peopleCount && <PeopleCountLine peopleCount={peopleCount} />}
      {error && <CardError>{error}</CardError>}
      {statusLabel && <CardStatus>{statusLabel}</CardStatus>}
      {!statusLabel && showCardActions && (
        <CardActions>
          <SecondaryButton auto disableMinWidth disabled={!canAnswer} onPress={handleCancel}>
            {cancelLabel}
          </SecondaryButton>
          {!operations && (
            <Button
              auto
              disableMinWidth
              color="primary"
              disabled={!canAnswer || hasInvalidField}
              loading={isSubmitting}
              onPress={handleHandRolledConfirm}
            >
              {confirmLabel}
            </Button>
          )}
        </CardActions>
      )}
    </CardContainer>
  );
};

interface RenderFieldOptions {
  canAnswer: boolean;
  editedValues: Record<string, string | boolean>;
  secretValues: Record<string, string>;
  setEditedValues: React.Dispatch<React.SetStateAction<Record<string, string | boolean>>>;
  setSecretValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  secretPlaceholder: string;
}

const renderFieldInput = (field: CardField, options: RenderFieldOptions) => {
  const { canAnswer, editedValues, secretValues, setEditedValues, setSecretValues, secretPlaceholder } = options;

  if (field.sensitive) {
    return (
      <FieldInput
        type="password"
        autoComplete="off"
        placeholder={secretPlaceholder}
        value={secretValues[field.name] ?? ''}
        disabled={!canAnswer}
        onChange={(e) => setSecretValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
      />
    );
  }

  if (isBooleanField(field)) {
    const checked = field.name in editedValues ? !!editedValues[field.name] : !!field.value;
    return (
      <FieldCheckbox
        type="checkbox"
        checked={checked}
        disabled={!canAnswer}
        onChange={(e) => setEditedValues((prev) => ({ ...prev, [field.name]: e.target.checked }))}
      />
    );
  }

  if (field.fixed) {
    return (
      <FieldJsonValue>
        {isStructuredField(field) ? JSON.stringify(field.value) : `${field.value as string}`}
      </FieldJsonValue>
    );
  }

  const displayValue = formatDisplayFieldValue(field);
  if (displayValue !== null) {
    return <FieldJsonValue>{displayValue}</FieldJsonValue>;
  }

  if (isStructuredField(field)) {
    return <FieldJsonValue>{JSON.stringify(field.value, null, 2)}</FieldJsonValue>;
  }

  const isNumber = field.schemaType === 'integer' || field.schemaType === 'number';
  const currentValue =
    field.name in editedValues ? String(editedValues[field.name]) : field.value == null ? '' : String(field.value);
  return (
    <FieldInput
      type={isNumber ? 'number' : 'text'}
      value={currentValue}
      disabled={!canAnswer}
      onChange={(e) => setEditedValues((prev) => ({ ...prev, [field.name]: e.target.value }))}
    />
  );
};

const isFieldInvalid = (
  field: CardField,
  editedValues: Record<string, string | boolean>,
  secretValues: Record<string, string>,
): boolean => {
  if (field.fixed || isBooleanField(field) || isStructuredField(field) || formatDisplayFieldValue(field) !== null)
    return false;
  const trimmed = readFieldStringValue(field, editedValues, secretValues).trim();
  if (field.required && trimmed === '') return true;
  return isNumericField(field) && trimmed !== '' && !isNumericValueValid(trimmed, field);
};

const readFieldStringValue = (
  field: CardField,
  editedValues: Record<string, string | boolean>,
  secretValues: Record<string, string>,
): string => {
  if (field.sensitive) return secretValues[field.name] ?? '';
  if (field.name in editedValues) return String(editedValues[field.name]);
  return field.value == null ? '' : String(field.value);
};

const isValueMissing = (value: unknown, field: CardField): boolean => {
  if (!field.required) return false;
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  return Array.isArray(value) && value.length === 0;
};

const hasExpired = (expiresAt: number | undefined): boolean => !!expiresAt && Date.now() > expiresAt * 1000;

const fetchSecureInputKey = async (apolloClient: ReturnType<typeof useApolloClient>) => {
  const { data } = await apolloClient.query<AgentSecureInputKeyQuery>({
    query: AgentSecureInputKeyDocument,
    fetchPolicy: 'network-only',
  });
  return data?.appConfiguration?.agentSecureInputKey ?? null;
};

export default MutationConfirmationCard;
